/**
 * Text-to-Speech Service
 * 
 * Uses the external audio speech API to generate and play audio from text.
 */

import chatService from './chatService';

// Spec hard limit
const MAX_TEXT_LENGTH = 4096;

class TTSService {
  constructor() {
    this.isPlaying = false;
    this.currentMessageId = null;
    this.stateChangeListeners = [];
    this.audioElement = null;
    this.audioUrl = null;
    // Monotonic counter so out-of-order responses can be discarded.
    this.requestSeq = 0;

    console.log('🔊 TTS Service initialized');
  }

  isConfigured() {
    return true;
  }

  async speak(text, messageId) {
    console.log(`🎯 [TTS] speak() called for message ID: ${messageId}`);

    if (!text || !text.trim()) {
      return { success: false, error: 'Empty text' };
    }

    // Stop any currently playing/loading audio (also bumps the seq via stop()).
    this.stop();

    if (!chatService.accessToken) {
      console.warn('⚠️ No access token available for TTS');
      return { success: false, error: 'No access token' };
    }

    // Truncate to API limit. The spec rejects >4096 with 400, so be defensive.
    const safeText = text.length > MAX_TEXT_LENGTH ? text.slice(0, MAX_TEXT_LENGTH) : text;
    if (text.length > MAX_TEXT_LENGTH) {
      console.warn(`⚠️ [TTS] Text truncated from ${text.length} to ${MAX_TEXT_LENGTH} chars`);
    }

    const seq = ++this.requestSeq;
    this.isPlaying = true;
    this.currentMessageId = messageId;
    this.notifyStateChange(messageId, 'loading');

    try {
      const response = await fetch(`${chatService.apiBaseUrl}/v1/external/audio/speech`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${chatService.shareToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: safeText,
          session_token: chatService.accessToken
        })
      });

      // If a newer request started (or stop() was called) while we were
      // awaiting the network, drop this response.
      if (seq !== this.requestSeq) {
        console.log(`ℹ️ [TTS] Stale response for message ID: ${messageId}, ignoring`);
        return { success: false, error: 'Superseded' };
      }

      if (!response.ok) {
        let detail = '';
        try {
          const err = await response.json();
          detail = err?.error || err?.message || '';
        } catch {
          // ignore parse errors
        }
        throw new Error(`TTS API failed: ${response.status}${detail ? ` - ${detail}` : ''}`);
      }

      const blob = await response.blob();

      // Re-check after blob() since it may take additional time.
      if (seq !== this.requestSeq) {
        console.log(`ℹ️ [TTS] Stale blob for message ID: ${messageId}, ignoring`);
        return { success: false, error: 'Superseded' };
      }

      this.audioUrl = URL.createObjectURL(blob);
      this.audioElement = new Audio(this.audioUrl);

      this.audioElement.onended = () => {
        console.log(`✅ [TTS] Audio ended for message ID: ${messageId}`);
        if (seq === this.requestSeq) this.stop();
      };

      this.audioElement.onerror = (e) => {
        console.error(`❌ [TTS] Audio error for message ID: ${messageId}`, e);
        if (seq === this.requestSeq) this.stop();
      };

      // External pause (e.g., iOS tab backgrounded) - reflect stopped state.
      this.audioElement.onpause = () => {
        if (
          seq === this.requestSeq &&
          this.audioElement &&
          !this.audioElement.ended &&
          this.audioElement.currentTime > 0 &&
          this.audioElement.currentTime < this.audioElement.duration
        ) {
          console.log(`⏸️ [TTS] Audio paused externally for message ID: ${messageId}`);
          this.stop();
        }
      };

      this.notifyStateChange(messageId, 'playing');
      await this.audioElement.play();

      return { success: true };
    } catch (error) {
      // Suppress AbortError noise from interrupting play() with stop()
      if (error?.name !== 'AbortError') {
        console.error(`❌ [TTS] Error in speak() for message ID: ${messageId}`, error);
      }
      if (seq === this.requestSeq) this.stop();
      return { success: false, error };
    }
  }

  stop() {
    // Bump seq so any in-flight requests/promises bail out.
    this.requestSeq++;

    if (this.audioElement) {
      // Detach handlers before pausing to avoid recursive stop() via onpause.
      this.audioElement.onended = null;
      this.audioElement.onerror = null;
      this.audioElement.onpause = null;
      try {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
      } catch {
        // ignore
      }
      this.audioElement = null;
    }

    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = null;
    }

    if (this.currentMessageId !== null) {
      this.notifyStateChange(this.currentMessageId, 'stopped');
      this.currentMessageId = null;
    }

    this.isPlaying = false;
  }

  cleanup() {
    this.stop();
    this.stateChangeListeners = [];
  }

  clearCache() {
    // No cache in this implementation
  }

  onStateChange(callback) {
    this.stateChangeListeners.push(callback);
    return () => {
      this.stateChangeListeners = this.stateChangeListeners.filter(cb => cb !== callback);
    };
  }

  notifyStateChange(messageId, state) {
    this.stateChangeListeners.forEach(callback => callback(messageId, state));
  }

  getStatus() {
    return {
      isPlaying: this.isPlaying,
      currentMessageId: this.currentMessageId,
    };
  }
}

// Export singleton instance
export default new TTSService();
